import React, { useCallback, useMemo, useState } from "react";
import {
  Button,
  DataTableCellContent,
  DataTableCellTrigger,
  DataTableEditableTextCell,
  DataTablePopoverCell,
  DataTablePopoverTrigger,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  createColumnHelper,
  Box,
  Icon,
  Text,
  type DataTableColumnDef,
} from "@salestrak/ui";
import type { IssueRow, StageOption } from "#app/lib/data";
import {
  EntityGroupTag,
  EntitySelectionHover,
  EntityTag,
  useEntityDetail,
  type EntityData,
} from "#app/components/entity";
import { ENTITY_KIND_ICON, entityFromAssigneeRef } from "#app/lib/entity-visuals";
import {
  StageSelector,
  AssigneeSelector,
  IssueTypeSelector,
} from "#app/components/entity-selectors";
import { WorkflowMenuCell } from "#app/components/issues/move-workflow-picker";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { TeamsPickerContent } from "#app/components/teams/teams-picker";
import { AgeLabel, useClockRunning } from "#app/components/age/age-label";
import { AddressValueCell } from "#app/components/fields/address-value-cell";
import { AutomationTouchMarker } from "#app/components/automations/automation-touch-marker";
import { IssueRenameAction } from "#app/components/issue-actions/issue-rename-action";
import { useIssueRenameOptional } from "#app/components/issues/issue-rename-context";
import type { Omittable } from "@salestrak/ui";

/**
 * Canonical issue column defs for the shared {@link IssuesTable} (used at every
 * scope — workflow, team, member). Consumers pass the output of
 * `useIssueColumns` to `<DataTable columns=>`. All entity cells go through
 * `#app/components/entity` — never hand-rolled avatar/label pairs.
 */

const h = createColumnHelper<IssueRow>();

// Inline cell wrappers — domain selectors in a cell popover. Multi-value
// pickers commit on every toggle; the cell answers while the picker is open.

function DraftPopoverCell({
  issueId,
  entityIds,
  onCommit,
  onSetPrimary,
  entities,
  triggerContent,
  renderSelector,
  emptyLabel,
  sheetTitle,
}: Omittable<{
  issueId: string;
  entityIds: string[];
  /** The empty cell's name: it shows only a glyph, which says nothing to a
   *  screen reader. */
  emptyLabel: string;
  /** The phone sheet's caption — the column's name. */
  sheetTitle: string;
  onCommit: (issueId: string, ids: string[]) => void;
  onSetPrimary?: (issueId: string, entityId: string) => void;
  entities?: EntityData[];
  /** Receives whether the picker popover is open, so the trigger chip can
   *  suppress its hover preview while the picker is up. */
  triggerContent?: (pickerOpen: boolean) => React.ReactNode;
  renderSelector: (
    value: string[],
    onValueChange: (ids: string[]) => void,
    onSetPrimary?: (entityId: string) => void,
    close?: () => void,
  ) => React.ReactNode;
}>) {
  // The picker's value while it is open. Every toggle COMMITS — the cell's
  // avatar stack and the row answer at once, the way a property row does —
  // and the local copy only keeps the picker's order steady between the
  // optimistic write and the cache catching up. Re-seeded on open so a
  // change made elsewhere while the picker was closed is what it shows.
  const [draft, setDraft] = useState(entityIds);
  const [pickerOpen, setPickerOpen] = useState(false);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setPickerOpen(open);
      if (open) setDraft(entityIds);
    },
    [entityIds],
  );

  const handleValueChange = useCallback(
    (ids: string[]) => {
      setDraft(ids);
      onCommit(issueId, ids);
    },
    [issueId, onCommit],
  );

  return (
    <DataTablePopoverCell
      aria-label={entityIds.length === 0 ? emptyLabel : undefined}
      sheetTitle={sheetTitle}
      onOpenChange={handleOpenChange}
      // No width pin: the popup hugs the inline selector's standard picker
      // width.
      contentProps={{
        align: "end",
        padding: "none",
      }}
      content={(close) =>
        renderSelector(
          draft,
          handleValueChange,
          onSetPrimary
            ? (entityId: string) => onSetPrimary(issueId, entityId)
            : undefined,
          close,
        )
      }
    >
      {entityIds.length === 0 ? (
        <Box width={24} height={24} align="center" justify="center">
          <Icon name="user-plus" size="md" color="muted" />
        </Box>
      ) : triggerContent ? (
        triggerContent(pickerOpen)
      ) : (
        // Cells never label an entity: one or many, the value is the 20px
        // ("xs") avatar stack, and the hover preview names it. The 24px "sm"
        // tier is for avatars standing alone in an icon column.
        <EntityGroupTag
          entities={entities ?? []}
          size="xs"
          interactive={false}
          hoverDisabled={pickerOpen}
        />
      )}
    </DataTablePopoverCell>
  );
}

export function AssigneePopoverCell({
  row,
  onAssigneesChange,
}: {
  row: IssueRow;
  onAssigneesChange: (issueId: string, entityIds: string[]) => void;
}) {
  const { openEntity } = useEntityDetail();

  const entityIds = useMemo(
    () => row.assignees.map((r) => r.entityId),
    [row.assignees],
  );
  const entities = useMemo(
    () => row.assignees.map(entityFromAssigneeRef),
    [row.assignees],
  );

  return (
    <DraftPopoverCell
      issueId={row.issueId}
      entityIds={entityIds}
      emptyLabel="Add assignees"
      sheetTitle="Assignees"
      onCommit={onAssigneesChange}
      onSetPrimary={(issueId, entityId) => {
        onAssigneesChange(issueId, [
          entityId,
          ...entityIds.filter((id) => id !== entityId),
        ]);
      }}
      entities={entities}
      triggerContent={
        row.assignees.length > 0
          ? (pickerOpen) => (
              // A cell value is the avatar stack whatever the count — one
              // assignee is a lone avatar, never avatar + name. Each avatar
              // hovers to its member preview; the cell owns the press.
              <EntityGroupTag
                entities={entities}
                variant="stack"
                size="xs"
                title="Assignees"
                interactive={false}
                hoverDisabled={pickerOpen}
              />
            )
          : undefined
      }
      renderSelector={(value, onChange, onSetPrimary, close) => (
        <AssigneeSelector
          variant="inline"
          mode="list"
          value={value}
          selectedRefs={row.assignees}
          onValueChange={onChange}
          onSetPrimary={onSetPrimary}
          onViewProfile={(userId) => {
            close?.();
            openEntity("user", userId, { via: "table_cell" });
          }}
        />
      )}
    />
  );
}

/** Id of the optional Stage column — hidden by default (the name cell's glyph
 *  carries the stage), shown from the Fields panel like a custom field. */
export const ISSUE_STAGE_COLUMN_ID = "stage";
/** The project type column — off by default on the full-page table (see
 *  `ISSUE_STAGE_COLUMN_ID`); the personal-home inventory table shows it. */
export const ISSUE_TYPE_COLUMN_ID = "issueType";
/** The Workflow column — where the project sits. On by default on the lists
 *  that span workflows; hidden inside one, where it would only repeat the
 *  page's own name. */
export const ISSUE_WORKFLOW_COLUMN_ID = "workflow";

/**
 * The optional Stage column's cell: the labelled stage chip, the whole cell a
 * draft-on-close picker. Off by default — the name cell's glyph already says
 * the stage — and shown from the Fields panel by a reader who wants the name
 * spelled out in its own column.
 */
function StagePopoverCell({
  row,
  stage,
  onChangeStage,
}: Omittable<{
  row: IssueRow;
  stage?: StageOption;
  onChangeStage: (issueId: string, stageId: string) => void;
}>) {
  // Track the picker so the chip's workflow hover preview stays down while
  // the stage picker popover is up (same suppression as the assignee cell).
  const [pickerOpen, setPickerOpen] = useState(false);

  const entity = useMemo<EntityData | null>(
    () =>
      stage
        ? {
            kind: "stage",
            id: stage.stageId,
            stage,
            workflowId: row.workflowId,
          }
        : null,
    [stage, row.workflowId],
  );

  return (
    <DataTablePopoverCell
      onOpenChange={setPickerOpen}
      contentProps={{ padding: "none" }}
      sheetTitle="Stage"
      content={(close) => (
        <StageSelector
          variant="inline"
          workflowId={row.workflowId}
          value={row.stageId}
          onValueChange={(stageId) => {
            onChangeStage(row.issueId, stageId);
            close();
          }}
          // The "…" stage settings closes the picker before its dialog;
          // inline, that close is this cell popover's.
          onOpenChange={(next) => {
            if (!next) close();
          }}
        />
      )}
    >
      <Box direction="row" align="center" gap="sm" overflow="hidden" fill="flex">
        {entity ? (
          <EntitySelectionHover entity={entity} disabled={pickerOpen}>
            <Box shrink>
              <EntityTag entity={entity} weight="regular" interactive={false} />
            </Box>
          </EntitySelectionHover>
        ) : (
          <Text size="sm" truncate={1}>
            Unknown
          </Text>
        )}
      </Box>
    </DataTablePopoverCell>
  );
}

/**
 * The Type column's editable cell: the type chip, the whole cell a picker over
 * the workspace's types. Picking another type reports to the host, which owns
 * the migrate dialog (the target type's required fields need values before the
 * move lands) — the cell never commits on its own.
 */
function IssueTypePopoverCell({
  row,
  onIssueTypeChange,
}: {
  row: IssueRow;
  onIssueTypeChange: (issue: IssueRow, issueTypeId: string) => void;
}) {
  const { issueTypes } = useWorkspaceData();
  return (
    <DataTablePopoverCell
      contentProps={{ padding: "none" }}
      sheetTitle="Type"
      content={(close) => (
        <IssueTypeSelector
          variant="inline"
          issueTypes={issueTypes}
          value={row.issueTypeId}
          onValueChange={(issueTypeId) => {
            onIssueTypeChange(row, issueTypeId);
            close();
          }}
        />
      )}
    >
      <IssueTypeCellValue row={row} />
    </DataTablePopoverCell>
  );
}

/** The Workflow cell's value: where the project sits, as the workflow's own
 *  tag — the same chip whether the cell edits or not. */
function WorkflowCellValue({ row }: { row: IssueRow }) {
  const { workflowMap } = useWorkspaceData();
  const workflow = workflowMap.get(row.workflowId);
  if (!workflow) return null;
  return (
    <EntityTag
      entity={{ kind: "workflow", id: workflow.workflowId, workflow }}
      interactive={false}
      size="sm"
    />
  );
}

/** The Type cell's value — the same chip whether the cell edits or not. */
export function IssueTypeCellValue({ row }: { row: IssueRow }) {
  if (!row.issueType) return null;
  return (
    <EntityTag
      entity={{
        kind: "issueType",
        id: row.issueType.issueTypeId,
        issueType: row.issueType,
      }}
      display="full"
      interactive={false}
      size="sm"
    />
  );
}

/**
 * The name cell's leading stage glyph — the reference list's status dot. At
 * rest it is the bare 16px stage pie on the cell's content line; hover swells
 * the 24px ghost square behind it (the row squares it). The tooltip is the
 * stage's name alone — every row here is a project, so "Stage:" would only
 * restate the glyph. Pressing opens the stage picker anchored to the glyph
 * through the table's shared popover host, so the whole cell stays the
 * open-project press. A row the viewer cannot edit keeps the glyph and its
 * tooltip without the press; a stage the roster has not resolved yet falls
 * back to the project kind glyph.
 */
export function IssueStageAction({
  issue,
  stage,
  onChangeStage,
}: Omittable<{
  issue: IssueRow;
  stage?: StageOption;
  onChangeStage: (issueId: string, stageId: string) => void;
}>) {
  // Gate the tooltip's POPUP on the picker, never its trigger chain — remounting
  // the trigger mid-interaction is what drops a Base UI hover.
  const [pickerOpen, setPickerOpen] = useState(false);

  const entity = useMemo<EntityData>(
    () =>
      stage
        ? { kind: "stage", id: stage.stageId, stage, workflowId: issue.workflowId }
        : {
            kind: "issue",
            id: issue.issueId,
            issue: { name: issue.name, icon: issue.icon },
            workflowId: issue.workflowId,
          },
    [stage, issue.issueId, issue.name, issue.icon, issue.workflowId],
  );
  const glyph = (
    <EntityTag
      display="visual"
      entity={entity}
      ghost={false}
      interactive={false}
      padded={false}
      size="sm"
    />
  );
  const label = stage?.name ?? "Stage";

  if (!issue.viewerCanEdit) {
    return (
      <Tooltip>
        <TooltipTrigger
          render={
            <Box
              width={24}
              height={24}
              align="center"
              justify="center"
              shrink={false}
              aria-label={`Stage: ${label}`}
            />
          }
        >
          {glyph}
        </TooltipTrigger>
        <TooltipPopup side="top">{label}</TooltipPopup>
      </Tooltip>
    );
  }

  return (
    <Tooltip>
      <DataTablePopoverTrigger
        onOpenChange={setPickerOpen}
        contentProps={{ padding: "none" }}
        sheetTitle="Stage"
        content={(close) => (
          <StageSelector
            variant="inline"
            workflowId={issue.workflowId}
            value={issue.stageId}
            onValueChange={(stageId) => {
              onChangeStage(issue.issueId, stageId);
              close();
            }}
            onOpenChange={(next) => {
              if (!next) close();
            }}
          />
        )}
        render={
          <TooltipTrigger
            render={
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label={`Stage: ${label}`}
              />
            }
          >
            {glyph}
          </TooltipTrigger>
        }
      />
      {pickerOpen ? null : <TooltipPopup side="top">{label}</TooltipPopup>}
    </Tooltip>
  );
}

function AddressCell({
  issue,
  onAddressSelect,
  onAddressClear,
}: {
  issue: IssueRow;
  onAddressSelect: (issue: IssueRow, wholeAddress: string) => void;
  onAddressClear: (issue: IssueRow) => void;
}) {
  const handleSelect = useCallback(
    (wholeAddress: string) => onAddressSelect(issue, wholeAddress),
    [onAddressSelect, issue],
  );
  const handleClear = useCallback(
    () => onAddressClear(issue),
    [onAddressClear, issue],
  );

  return (
    <AddressValueCell
      wholeAddress={issue.address?.wholeAddress}
      latLng={issue.address?.latLng}
      addressId={issue.address?.addressId}
      // A table cell hard-clips: the row-style end bleed would cut the
      // outline's corners square.
      bleedEnd={false}
      onSelect={handleSelect}
      onClear={handleClear}
      readOnly={!issue.viewerCanEdit}
    />
  );
}

/** Teams cell display rule (drawer trigger parity): 0 → the muted teams
 *  glyph as the add affordance; 1 → tile + name; 2+ → the square-tile stack
 *  (squares are teams, circles are people). The whole cell opens the shared
 *  teams picker, which applies per change — no draft-on-close commit.
 *  The empty glyph is the OUTLINE kind mark, never the part-filled property
 *  label glyph: solid is what a value looks like (the tiles), so an empty
 *  cell must not paint one — the same rule the assignee cell's `user-plus`
 *  follows. */
function TeamsCellValue({
  row,
  pickerOpen = false,
}: {
  row: IssueRow;
  pickerOpen?: boolean;
}) {
  // `row.teams` (not a `?? []` local) as the dep: the fallback would mint a
  // fresh array every render and defeat the memo.
  const entities = useMemo(
    () =>
      (row.teams ?? []).map((t) => ({
        kind: "team" as const,
        id: t.teamId,
        team: t,
      })),
    [row.teams],
  );
  const teams = row.teams ?? [];
  if (teams.length === 0) {
    return (
      <Box width={24} height={24} align="center" justify="center">
        <Icon name={ENTITY_KIND_ICON.team} size="md" color="muted" />
      </Box>
    );
  }
  // One team or many, the cell is the tile stack — never tile + name. Each
  // tile hovers to its team preview; the cell owns the press.
  return (
    <EntityGroupTag
      entities={entities}
      variant="stack"
      size="xs"
      title="Teams"
      interactive={false}
      hoverDisabled={pickerOpen}
    />
  );
}

function TeamsPopoverCell({
  row,
  onTeamsChange,
}: {
  row: IssueRow;
  onTeamsChange: (issue: IssueRow, teamIds: string[]) => void;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  return (
    <DataTablePopoverCell
      aria-label={(row.teams ?? []).length === 0 ? "Add teams" : undefined}
      onOpenChange={setPickerOpen}
      contentProps={{ padding: "none" }}
      sheetTitle="Teams"
      content={() => (
        <TeamsPickerContent
          value={(row.teams ?? []).map((t) => t.teamId)}
          onValueChange={(ids) => onTeamsChange(row, ids)}
          selectedTeams={row.teams ?? []}
        />
      )}
    >
      <TeamsCellValue row={row} pickerOpen={pickerOpen} />
    </DataTablePopoverCell>
  );
}

export type UseIssueColumnsParams = Omittable<{
  availableStages: StageOption[];
  onOpenIssue: (issue: IssueRow) => void;
  onChangeStage: (issueId: string, stageId: string) => void;
  onAssigneesChange?: (issueId: string, entityIds: string[]) => void;
  /** Teams-property editing — absent on read-only surfaces, which keep the
   *  same three-state cell value without the picker. */
  onTeamsChange?: (issue: IssueRow, teamIds: string[]) => void;
  onAgePress: (issue: IssueRow) => void;
  /** Type editing — the host opens the migrate dialog for the picked type.
   *  Absent on read-only surfaces, which keep the chip without the picker. */
  onIssueTypeChange?: (issue: IssueRow, issueTypeId: string) => void;
  /** Moving between workflows — the row menu's "Move to Workflow…" as the
   *  Workflow cell's picker. Absent on read-only surfaces. */
  onWorkflowChange?: (issue: IssueRow, workflowId: string, stageId: string) => void;
  /** Address editing is optional — when either handler is absent the Address
   *  column is omitted entirely (e.g. the dashboard `entity-list` widget, which
   *  never shows it). */
  onAddressSelect?: (issue: IssueRow, wholeAddress: string) => void;
  onAddressClear?: (issue: IssueRow) => void;
  /** Mounts the hover Rename action on the name column. Pass true only from a
   *  host that provides IssueRenameControls (the issues list); widget tables
   *  have no rename surface and keep the plain name cell. */
  renameAction?: boolean;
}>;


/**
 * The name cell: the renaming row swaps its press-target cell for a text
 * editor (the two cannot coexist — a click in the input would open the
 * project); every other row renders the hover-action composition passed in.
 * The stage action stays seated ahead of the editor — it is the row's, not
 * the press target's, so a rename must not make it vanish.
 */
function IssueNameCell({
  issue,
  stageAction,
  rest,
}: {
  issue: IssueRow;
  stageAction: React.ReactNode;
  rest: React.ReactNode;
}) {
  const rename = useIssueRenameOptional();
  if (rename && rename.renamingIssueId === issue.issueId) {
    return (
      <DataTableEditableTextCell
        value={issue.name}
        leading={stageAction}
        editing
        // No active ring or fill — the swap to a caret is the editing
        // signal, and the row's own chrome already frames the cell.
        outline={false}
        weight="medium"
        aria-label={`Rename ${issue.name}`}
        onCommit={(next) => rename.onRenameCommit(issue, next)}
        onEditingChange={(editing) => {
          if (!editing) rename.onRenamingEnd();
        }}
      />
    );
  }
  return rest;
}

/**
 * A read-only entity cell. The chips inside hover to their preview and open
 * their entity, but the cell edits nothing, so it wears no ring — the ring is
 * a promise the cell edits, and a preview or a detail view is not an edit.
 * Shared with every table that shows an entity chip in a cell without editing
 * it (the outcome drilldown's "Updated by"), so the column reads the same
 * everywhere.
 */
export function ReadOnlyEntityCell({ children }: { children: React.ReactNode }) {
  return <DataTableCellContent>{children}</DataTableCellContent>;
}

/** The Follow up cell. A running clock reads as the tinted count ALONE — no
 *  pulse glyph: the column's tint already carries the tier, and a glyph on
 *  every row would restate it. Rows with no Follow up started keep the muted
 *  pulse as a pressless ghost — the glyph is the whole cell there, since
 *  nothing is counting. The ghost wears no trigger, or pressing the blank
 *  cell would open the posting dialog for a Project that never asks for
 *  updates. "Off" is `useClockRunning`'s answer (effective cycle, not the
 *  stored status alone). */
function FollowUpCell({
  issue,
  onAgePress,
}: {
  issue: IssueRow;
  onAgePress: (issue: IssueRow) => void;
}) {
  const running = useClockRunning(issue.age, issue.stageId);
  if (!running) {
    return (
      <DataTableCellContent>
        <AgeLabel age={issue.age} stageId={issue.stageId} showOff />
      </DataTableCellContent>
    );
  }
  return (
    // The chip is the fact and the cell is the press: it posts an update —
    // the aria-label names the action.
    <DataTableCellTrigger
      aria-label={`Post an Update for ${issue.name}`}
      onClick={() => onAgePress(issue)}
    >
      <DataTableCellContent>
        <AgeLabel age={issue.age} stageId={issue.stageId} glyph={false} />
      </DataTableCellContent>
    </DataTableCellTrigger>
  );
}

export function useIssueColumns({
  availableStages,
  onOpenIssue,
  onChangeStage,
  onAssigneesChange,
  onTeamsChange,
  onAgePress,
  onIssueTypeChange,
  onWorkflowChange,
  onAddressSelect,
  onAddressClear,
  renameAction = false,
}: UseIssueColumnsParams): DataTableColumnDef<IssueRow>[] {
  const stageById = useMemo(() => {
    const map = new Map<string, StageOption>();
    for (const s of availableStages) map.set(s.stageId, s);
    return map;
  }, [availableStages]);

  return useMemo<DataTableColumnDef<IssueRow>[]>(() => {
    // Stage glyph, then the name: the stage rides the name cell as its
    // leading action (see IssueStageAction) in BOTH the press-target and the
    // renaming cell; the Stage COLUMN below is an opt-in second reading of
    // the same fact, off by default.
    const stageAction = (issue: IssueRow) => (
      <IssueStageAction
        issue={issue}
        stage={stageById.get(issue.stageId)}
        onChangeStage={onChangeStage}
      />
    );
    const nameColumn = h.hoverAction({
      accessorKey: "name",
      header: "Name",
      enableSorting: true,
      // Wide enough that a Project's name reads whole before anyone drags
      // it (people widened it to ~540–660px in their own lists); the ratio
      // cap keeps it under half the table on any viewport, and on narrow
      // (mobile) containers the default clamps down toward the floor. A
      // dragged width, saved per list, still wins.
      meta: { width: 480, minWidth: 300, maxWidthRatio: 0.5 },
      content: (issue) => (
        <Box direction="row" align="center" gap="sm" minWidth={0} shrink>
          {stageAction(issue)}
          <Box minWidth={0} shrink>
            <EntityTag
              entity={{
                kind: "issue",
                id: issue.issueId,
                issue: { name: issue.name, icon: issue.icon },
                workflowId: issue.workflowId,
              }}
              display="label"
              weight="medium"
              interactive={false}
            />
          </Box>
          <AutomationTouchMarker issueId={issue.issueId} issueName={issue.name} />
        </Box>
      ),
      onClickAction: (issue) => onOpenIssue(issue),
      actionLabel: (issue) => `Open ${issue.name}`,
      ...(renameAction
        ? { actions: (issue: IssueRow) => <IssueRenameAction issue={issue} /> }
        : null),
    });
    // The renaming swap lives INSIDE the cell (IssueNameCell reads the
    // rename context), so the column def stays stable across rename
    // start/end and no cell remounts.
    const restNameCell = nameColumn.cell;
    const nameWithRename: DataTableColumnDef<IssueRow> = {
      ...nameColumn,
      cell: (ctx) => (
        <IssueNameCell
          issue={ctx.row.original}
          stageAction={stageAction(ctx.row.original)}
          rest={typeof restNameCell === "function" ? restNameCell(ctx) : null}
        />
      ),
    };

    return [
      nameWithRename,

      // Off by default — hosts list it in `defaultHiddenColumnIds`; the
      // Fields panel shows it. Sortable: the column id IS the server's
      // `stage` orderBy key (pipeline position, not stage name), so the
      // header sort sends the same key the Sort by row does. The accessor
      // only satisfies the table's sortability check — sorting is manual and
      // the cell reads the row itself.
      h.accessor({
        id: ISSUE_STAGE_COLUMN_ID,
        accessorKey: "stageId",
        header: "Stage",
        enableSorting: true,
        meta: { minWidth: 160, maxWidth: 400 },
        cell: ({ row }) => (
          <StagePopoverCell
            row={row.original}
            stage={stageById.get(row.original.stageId)}
            onChangeStage={onChangeStage}
          />
        ),
      }),

      // Shown by default on the lists that span workflows (a team's, a
      // member's, Shared); hidden inside one workflow, where every row would
      // repeat the page's own name (`defaultHiddenColumnIds`).
      h.display({
        id: ISSUE_WORKFLOW_COLUMN_ID,
        header: "Workflow",
        // A workflow name is a phrase, not a word: room for the tag to read
        // whole before anyone drags it.
        meta: { width: 220, minWidth: 140, maxWidth: 400 },
        cell: ({ row }) =>
          onWorkflowChange && row.original.viewerCanEdit ? (
            <WorkflowMenuCell
              workflowId={row.original.workflowId}
              onMove={(workflowId, stageId) => onWorkflowChange(row.original, workflowId, stageId)}
            >
              <WorkflowCellValue row={row.original} />
            </WorkflowMenuCell>
          ) : (
            <DataTableCellContent>
              <WorkflowCellValue row={row.original} />
            </DataTableCellContent>
          ),
      }),

      h.display({
        id: "age",
        header: "Follow up",
        meta: { minWidth: 100, maxWidth: 400 },
        cell: ({ row }) => <FollowUpCell issue={row.original} onAgePress={onAgePress} />,
      }),

      // The project's type as its chip. Editable rows pick from the whole
      // cell; the host then runs the migrate dialog, since the target type's
      // required fields move with it. A project without a type shows an empty
      // cell, not a placeholder — absence is the value.
      h.display({
        id: ISSUE_TYPE_COLUMN_ID,
        header: "Type",
        meta: { minWidth: 120, maxWidth: 400 },
        cell: ({ row }) => {
          if (!row.original.issueType) return null;
          if (onIssueTypeChange && row.original.viewerCanEdit) {
            return (
              <IssueTypePopoverCell
                row={row.original}
                onIssueTypeChange={onIssueTypeChange}
              />
            );
          }
          return (
            <ReadOnlyEntityCell>
              <IssueTypeCellValue row={row.original} />
            </ReadOnlyEntityCell>
          );
        },
      }),

      h.display({
        id: "assignees",
        header: "Assignees",
        // 200 fits the avatar stack comfortably; resize can go tighter.
        meta: { width: 200, minWidth: 100, maxWidth: 400 },
        cell: ({ row }) =>
          onAssigneesChange ? (
            <AssigneePopoverCell
              row={row.original}
              onAssigneesChange={onAssigneesChange}
            />
          ) : (
            // Read-only: the chips still hover to a preview and open a
            // profile, but nothing here edits the assignees, so no ring.
            <ReadOnlyEntityCell>
              {/* Same value as the editable trigger: the label-less avatar
               * stack, one assignee or many. Avatars hover to their preview
               * and press to open the member. */}
              <EntityGroupTag
                entities={row.original.assignees.map(entityFromAssigneeRef)}
                variant="stack"
                size="xs"
                title="Assignees"
              />
            </ReadOnlyEntityCell>
          ),
      }),

      h.display({
        id: "teams",
        header: "Teams",
        meta: { minWidth: 160, maxWidth: 400 },
        cell: ({ row }) =>
          onTeamsChange ? (
            <TeamsPopoverCell row={row.original} onTeamsChange={onTeamsChange} />
          ) : (
            <ReadOnlyEntityCell>
              <TeamsCellValue row={row.original} />
            </ReadOnlyEntityCell>
          ),
      }),

      ...(onAddressSelect && onAddressClear
        ? [
            h.display({
              id: "address",
              header: "Address",
              // 250 fits a one-line street address; resize can go tighter.
              meta: { width: 250, minWidth: 100, maxWidth: 400 },
              cell: ({ row }) => (
                <AddressCell
                  issue={row.original}
                  onAddressSelect={onAddressSelect}
                  onAddressClear={onAddressClear}
                />
              ),
            }),
          ]
        : []),

      // Dates keep the helper's 120 as their best-fit default but may be
      // squeezed to the shared 100 floor.
      h.date({
        accessorKey: "creationDate",
        header: "Created",
        meta: { width: 120, minWidth: 100, maxWidth: 400 },
      }),
      h.date({
        accessorKey: "lastUpdateDate",
        header: "Updated",
        meta: { width: 120, minWidth: 100, maxWidth: 400 },
      }),
    ];
  }, [
    stageById,
    onOpenIssue,
    onChangeStage,
    onAssigneesChange,
    onTeamsChange,
    onAgePress,
    onIssueTypeChange,
    onWorkflowChange,
    onAddressSelect,
    onAddressClear,
    renameAction,
  ]);
}
